import { Component, Input, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { AppDateAdapter } from '@otrosProveedoresV6/date-format.adapter';
import { MatPaginatorIntlEses } from '@otrosProveedoresV6/ES-es.paginator';
import { Translator } from '@sharedV11/services/translator/translator.service';
import { getLocale } from '@sharedV11/utils/factories';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const MY_FORMATS = {
  parse: {
    dateInput: "DD-MM-YYYY",
  },
  display: {
    dateInput: "DD-MM-YYYY",
    monthYearLabel: "DD-MM-YYYY",
    dateA11yLabel: "DD-MM-YYYY",
    monthYearA11yLabel: "DD-MM-YYYY",
  },
};

@Component({
  selector: 'app-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: AppDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlEses },
    {
      provide: LOCALE_ID,
      useFactory: getLocale,
      deps: [Translator],
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: getLocale,
      deps: [Translator],
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
})
export class DateRangeComponent implements OnInit, OnDestroy {
  @Input() group!: FormGroup;
  @Input() dateStartControlName = 'FechaInicio';
  @Input() dateEndControlName = 'FechaFin';
  @Input() startDateInvalidKey = 'FechaInicioMoreThanFechaFin';
  @Input() endDateInvalidKey = 'FechaFinLessThanFechaInicio';
  @Input() translation;
  @Input() minDate: Date = new Date();

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    try {
      const dateStartControl = this.group.get(this.dateStartControlName);
      const dateEndControl = this.group.get(this.dateEndControlName);

      merge(
        dateStartControl?.valueChanges,
        dateEndControl?.valueChanges
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.validateDates())


    } catch (error) {
      console.info('Could not to get controls for dates start and end: ', error)
    }

  }

  private validateDates(): void {
    const dateStartControl = this.group.get(this.dateStartControlName);
    const dateEndControl = this.group.get(this.dateEndControlName);

    const start = dateStartControl?.value;
    const end = dateEndControl?.value;

    if (!start || !end) {
      this.removeError(dateStartControl, this.startDateInvalidKey);
      this.removeError(dateEndControl, this.endDateInvalidKey);
      
      return;
    }

    if (new Date(start) > new Date(end)) {
      this.addError(dateStartControl, this.startDateInvalidKey)

    } else {
      this.removeError(dateStartControl, this.startDateInvalidKey);
    }
    
    if (new Date(end) < new Date(start)) {
      this.addError(dateEndControl, this.endDateInvalidKey)
    } else {
      this.removeError(dateEndControl, this.endDateInvalidKey);
    }
  }

  private addError(control: AbstractControl, key: string) {
    const errors = control?.errors || {};
    errors[key] = true;
    control.setErrors(errors);
  }

  private removeError(control: AbstractControl, key: string) {
    if (!control.errors) return;

    const errors = { ...control.errors };
    delete errors[key];

    if (Object.keys(errors).length === 0) {
      control.setErrors(null);
    } else {
      control.setErrors(errors);
    }
  }

  public getErrorMessage(field: string) {
    const control = this.group.get(field);
    let errorMessage = 'Error';

    if (control && control.errors) {
      if (control.errors.required) {
        return this.translation["CampoRequerido"];
      }

      if (control?.errors[this.startDateInvalidKey]) {
        errorMessage = this.translation[this.startDateInvalidKey];
      } 

      if (control?.errors[this.endDateInvalidKey]) {
        errorMessage = this.translation[this.endDateInvalidKey];
      } 
    }

    return errorMessage;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
